import React, { useCallback, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { TradeRadio } from './TradeRadio';
import { useGetAllBroadcastEventsQuery } from 'common/store/modules/api/content-api';
import { filterAndModelEvents } from './util/filterAndModelEvents';
import { TradeRadioMiniPopup } from 'widgets/trade-radio/js/TradeRadioMiniPopup';
import { TradeRadioMiniPlayer } from 'widgets/trade-radio/js/TradeRadioMiniPlayer';

const SELECTORS = {
    ADHOC_NOTIFICIATION_WRAPPER: '.adhoc-notification-base'
};

const adhocNotificationWrapper = document.querySelector(
    SELECTORS.ADHOC_NOTIFICIATION_WRAPPER
);

export const TradeRadioLoader = ({
    config,
    widgetClass,
    anchorName,
    eventType,
    accountId,
    policyKey,
    playerId,
    isMiniPopup = false,
    isMiniPlayer = false
}) => {
    let configObj = {};
    try {
        configObj = JSON.parse(config);
    } catch (error) {
        console.error(
            '[TradeRadioLoader] There was a problem parsing the incoming config object',
            error
        );
    }

    const brightcoveProps = {
        anchorName,
        accountId,
        policyKey,
        playerId
    };

    const { event, refetch } = useGetAllBroadcastEventsQuery(
        {
            fromDate: luxon.DateTime.utc()
                .minus({ day: 1 })
                .startOf('hour')
                .toISO({ suppressMilliseconds: true }),
            pageSize: 100
        },
        {
            selectFromResult: ({ data }) => ({
                event: filterAndModelEvents(data, eventType)
            })
        }
    );

    // Store the last successful event to show "event finished" state even when new fetch returns null
    const [lastSuccessfulEvent, setLastSuccessfulEvent] = useState(null);
    const lastEventIdRef = useRef(null);

    // Update lastSuccessfulEvent when we get a new non-null event
    useEffect(() => {
        if (event && event.id !== lastEventIdRef.current) {
            setLastSuccessfulEvent(event);
            lastEventIdRef.current = event.id;
        }
    }, [event]);

    const handleStreamEnded = useCallback(() => {
        refetch();
    }, [refetch]);

    // Use the current event if available, otherwise fall back to the last successful event
    const displayEvent = event || lastSuccessfulEvent;

    useEffect(() => {
        // If no present or upcoming streams, move adhoc popups to bottom of container
        if (!event && adhocNotificationWrapper) {
            adhocNotificationWrapper.style.bottom = '0px';
        }
    }, [event]);

    if (isMiniPopup) {
        return event ? (
            <TradeRadioMiniPopup
                event={displayEvent}
                config={configObj}
                onStreamEnded={handleStreamEnded}
            />
        ) : null;
    }

    return displayEvent ? (
        isMiniPlayer ? (
            <TradeRadioMiniPlayer
                event={displayEvent}
                config={configObj}
                brightcoveProps={brightcoveProps}
                onStreamEnded={handleStreamEnded}
            />
        ) : (
            <TradeRadio
                event={displayEvent}
                config={configObj}
                brightcoveProps={brightcoveProps}
                widgetClass={widgetClass}
            />
        )
    ) : null;
};

TradeRadioLoader.propTypes = {
    config: PropTypes.string.isRequired,
    widgetClass: PropTypes.string,
    anchorName: PropTypes.string,
    eventType: PropTypes.string,
    accountId: PropTypes.string,
    policyKey: PropTypes.string,
    playerId: PropTypes.string,
    isMiniPopup: PropTypes.bool,
    isMiniPlayer: PropTypes.bool
};
